import { ApiError } from '../services/apiClient';
import {
  GENERIC_ERROR_PREFIX,
  NOT_APPLICABLE,
  NO_DATA_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
  TOKEN_NOT_ELIGIBLE_FOR_REFRESH,
} from './constants';
import type { ErrorMessage } from '../types';

/**
 * Port of `utils.js:getErrorMessage` (utils.js:73-123).
 *
 * The four branches of the original keyed off axios's error shape. Their equivalents
 * with `fetch` are:
 *
 * | old branch          | old meaning                        | here                       |
 * |---------------------|------------------------------------|----------------------------|
 * | `error.response`    | server replied with a non-2xx      | `kind === 'response'`      |
 * | `error.request`     | request sent, no response arrived   | `kind === 'network'`       |
 * | `message === TOKEN…`| expired token, refresh not allowed | `kind === 'token-expired'` |
 * | else                | anything else thrown               | everything else            |
 *
 * The `error.request` branch is worth noting: it produced `status: error?.response?.status`
 * on an error that by definition has no `response`, so its status was always `undefined`.
 * Reproduced, because App.js and mainMenu.jsx both branch on `status === 401` /
 * `status === 404` to decide whether to show the modal, and `undefined` matches neither —
 * i.e. a network failure DOES show the modal. Normalising it to 0 would keep that
 * behavior, but only by coincidence; leaving it undefined keeps the equivalence exact.
 *
 * The message text itself is assembled as `prefix + " " + requestId`, exactly as the
 * original did, which is why two spaces appear before the id.
 */
export function getErrorMessage(error: unknown): ErrorMessage {
  const generic = (requestId: string): string => `${GENERIC_ERROR_PREFIX} ${requestId}`;

  if (error instanceof ApiError) {
    if (error.kind === 'response') {
      const requestId = error.requestId;

      if (error.status === 404) {
        return { errorMessage: NO_DATA_MESSAGE, status: 404 };
      }
      if (error.status === 401) {
        // The original's odd nested shape (utils.js:87-94). errorModel.jsx reads
        // `errorResponse.message.errorMessage`, so this nesting means a 401 renders
        // nothing — but App.js also suppresses the modal on 401, so it never gets
        // there. Preserved rather than flattened.
        return {
          status: 401,
          message: { errorMessage: generic(requestId), status: 401 },
        };
      }
      return { errorMessage: generic(requestId), status: error.status };
    }

    if (error.kind === 'network') {
      // status intentionally undefined — see the table above.
      return { errorMessage: generic(error.requestId), status: undefined };
    }

    if (error.kind === 'token-expired') {
      return { errorMessage: SESSION_EXPIRED_MESSAGE, status: 0 };
    }
  }

  // A plain Error carrying the sentinel — thrown by services/okta.ts:getToken before any
  // request is made, so it never becomes an ApiError.
  if (error instanceof Error && error.message === TOKEN_NOT_ELIGIBLE_FOR_REFRESH) {
    return { errorMessage: SESSION_EXPIRED_MESSAGE, status: 0 };
  }

  return { errorMessage: generic(NOT_APPLICABLE), status: 0 };
}

/**
 * `error: {status, message}` as App.js and mainMenu.jsx store it.
 *
 * Both files repeat the same rule verbatim at eight call sites: show the modal unless
 * the status is 401 or 404 (App.js:122-127). 401 is suppressed because the auth layer
 * handles it; 404 because "No Data To Display" is rendered inline instead.
 */
export function toErrorState(error: unknown): { status: boolean; message: ErrorMessage } {
  const message = getErrorMessage(error);
  return {
    status: message?.status === 401 ? false : message?.status === 404 ? false : true,
    message,
  };
}
